import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { useSelector } from "react-redux";

import { EmptyMessage, Loader, TextInput, Title } from "components";
import { AppConstants, ServiceConstants } from "constant";
import { useListHook } from "hooks";
import { ChatService } from "services";
import { ChatResponse } from "types";
import { AppUtils, StorageUtils } from "utils";

import { IRootState } from "config/redux";

import ChatUserItem from "./item";
import ChatUserNew from "./new";

const { STORAGE_CHAT_USER } = AppConstants;
const { REQUEST_CHATS_LIST_ID, REQUEST_CHATS_ONE_ID, REQUEST_CHATS_MESSAGES_READ_ID, REQUEST_PENDING } = ServiceConstants;

interface ChatUserViewProps {
  activeChat: ChatResponse | undefined;
  setActiveChat: (item: ChatResponse) => void;
}

const ChatUserView = ({ activeChat, setActiveChat }: ChatUserViewProps) => {

  const chatServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CHATS_LIST_ID]);
  const chatOneServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CHATS_ONE_ID]);
  const readAllServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CHATS_MESSAGES_READ_ID]);
  const { t } = useTranslation();
  const { page, content, updatePageable, resetList, totalPages, refreshList, setPage } = useListHook();
  const [search, setSearch] = useDebouncedState('', 500);
  const [chats, setChats] = useState<ChatResponse[]>([]);

  useEffect(() => {
    return(() => {
      setChats([]);
      resetList();
    });
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(chatServiceObject, REQUEST_CHATS_LIST_ID, (data) => {
      updatePageable(data);
      const id = StorageUtils.getObject(STORAGE_CHAT_USER)
      if (id) {
        StorageUtils.deleteKey(STORAGE_CHAT_USER);
        ChatService.chatsOne(parseInt(id));
      }
  });
    AppUtils.resolveServiceObject(readAllServiceObject, REQUEST_CHATS_MESSAGES_READ_ID, () => {
      setChats(chats.map(el => {
        const tempEl = { ...el };
        if (tempEl.id === activeChat?.id) tempEl.unread = 0
        return tempEl;
      }));
    });
    AppUtils.resolveServiceObject(chatOneServiceObject, REQUEST_CHATS_ONE_ID, setActiveChat);
  }, [chatServiceObject, readAllServiceObject]);

  useEffect(() => {
    if (page === undefined) {
      return;
    }
    if (page === -1) {
      resetList();
      return;
    }
    fetch();
  }, [page]);

  useEffect(() => {
    if (search === undefined) {
      return;
    }
    setChats([]);
    refreshList();
  }, [search]);

  useEffect(() => {
    if (content) {
      setChats(old => ([...old, ...content]));
      activateScrollListener();
    }
  }, [content]);

  const activateScrollListener = useCallback(() => {
    const objDiv = document.getElementById(`chats`);
    if (!objDiv) {
      return null;
    }
    if (page && page === totalPages - 1) {
      return null;
    }
    return objDiv.addEventListener('scroll', () => {
      const scrolled = objDiv.scrollHeight - objDiv.scrollTop - objDiv.clientHeight;
      if (scrolled < 20) {
        deactivateScrollListener();
        setPage((page ?? 0) + 2);
      }
    });
  }, [page, totalPages]);

  useEffect(() => {
    if (!activeChat) {
      return;
    }
    const index = chats.findIndex(el => el.user.id === activeChat.user.id);
    if (index !== -1) {
      const prevChat = chats[index];
      if (prevChat.lastMessage?.id === activeChat.lastMessage?.id) {
        return;
      }
    }
    const tempArray = chats.filter(el => el.user.id !== activeChat.user.id);
    tempArray.unshift(activeChat);
    setChats(tempArray);
  }, [activeChat])

  const deactivateScrollListener = useCallback(() => {
    document.getElementById(`chats`)?.removeEventListener('scroll', activateScrollListener);
  }, []);

  const users = useMemo(() => {
    if (!chats) {
      return <></>
    }
    return chats.map((el: ChatResponse, index: number) =>
      <ChatUserItem item={el} index={index} t={t} activeChat={activeChat} setActiveChat={setActiveChat} />)
  }, [chats, activeChat]);

  const fetch = useCallback(() => ChatService.chatsAll({ page: page ?? 0, search: search }) , [page, search]);
  const isLoading = useCallback(() => chatServiceObject && chatServiceObject.status === REQUEST_PENDING && page === 0, [chatServiceObject, page]);

  const setActiveChatWrapper = useCallback((item: ChatResponse) => {
    const found = chats.findIndex(el => el.user.id === item.user.id);
    if (found === -1) {
      setChats(old => ([item, ...old.filter(el => el.id)]));
      setActiveChat(item);
    } else {
      setActiveChat(chats[found]);
    }
  }, [chats]);

  return (
    <Box bg={'var(--mantine-color-gray-1)'} h={'calc(100vh - 55px)'} id={'chats'} style={{ overflowY: 'auto' }}>
      <Flex
        justify={'space-between'}
        align={'center'}
        p={'md'}
      >
        <Title order={5} mb={6} pl={'xs'}>{t('chat.title')}</Title>
        <ChatUserNew onSelect={setActiveChatWrapper} />
      </Flex>
      <TextInput
        placeholder={t('chat.users.search')}
        onChange={(e) => setSearch(e.target.value)}
        px={'lg'}
      />
      <Box mt={'md'}>
        <Box pos={'relative'}>
          <Loader overlay={true} visible={content === undefined || isLoading()} />
          {content && (
            content.length === 0 ?
              <EmptyMessage message={t('chat.list.empty')} />
            :
            users
          )}
          </Box>
        </Box>
    </Box>
  );
}

export default ChatUserView;